import type { Checkbox as PrimeCheckbox, CheckboxChangeEvent } from "primereact/checkbox";
import { classNames } from "primereact/utils";
import { useEffect, useImperativeHandle, useMemo, useRef } from "react";
import type { ForwardedRef } from "react";

import { useStableRest } from "../../utils/use-stable-rest";
import { primeIcon } from "../icon";
import type { CheckboxProps, CheckboxViewModel } from "./types-checkbox";

/** Ícones do Figma (14) no lugar dos do Prime: check da caixa marcada e minus do estado misto. */
const CHECK_ICONS = {
  sm: primeIcon("check", 12),
  md: primeIcon("check", 14),
  lg: primeIcon("check", 16),
};
const MINUS_ICONS = {
  sm: primeIcon("minus", 12),
  md: primeIcon("minus", 14),
  lg: primeIcon("minus", 16),
};

/** ViewModel do Checkbox: estado misto (o Prime não tem), `id` → `inputId` e o `ref` da instância do Prime; sem JSX. */
export function useCheckbox(
  {
    indeterminate = false,
    size = "md",
    id,
    inputId,
    icon,
    checked,
    onChange,
    className,
    ...props
  }: CheckboxProps,
  ref: ForwardedRef<PrimeCheckbox>,
): CheckboxViewModel {
  const rest = useStableRest(props);
  const checkboxRef = useRef<PrimeCheckbox>(null);

  // A instância do Prime é a do `ref` da tela (`focus`, `getElement`, `getInput`).
  useImperativeHandle(ref, () => checkboxRef.current as PrimeCheckbox);

  // O Prime não tem estado misto: reflete no `<input>` nativo para leitores de tela.
  useEffect(() => {
    const input = checkboxRef.current?.getInput();
    if (input) input.indeterminate = indeterminate;
  }, [indeterminate]);

  const checkboxProps = useMemo(
    () => ({
      icon: icon ?? (indeterminate ? MINUS_ICONS[size] : CHECK_ICONS[size]),
      ...rest,
      className: classNames(size !== "md" && `p-checkbox-${size}`, className),
      inputId: inputId ?? id,
      checked: checked || indeterminate,
      onChange: onChange
        ? (event: CheckboxChangeEvent) =>
            onChange(indeterminate ? { ...event, checked: true } : event)
        : undefined,
    }),
    [icon, indeterminate, size, className, rest, inputId, id, checked, onChange],
  );

  return { checkboxRef, checkboxProps };
}
